import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { doc, getDoc, setDoc, writeBatch, collection, getDocs, Timestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from './firebase';
import unifiedData from './data.js';
import dataScience from './data/dataScience.js';
import dataDivertissement from './data/dataDivertissement.js';
import dataSport from './data/dataSport.js';
import NobelPrize from './data/dataNobelPrize';
import dataLiterature from './data/dataLiterature.js';
import dataArt from './data/dataArt.js';
import dataGeography from './data/dataGeography';
import Leaderboard from './Leaderboard';
import QuizQuestion from './QuizQuestion';
import QuizResults from './QuizResults';
import './Quizz.css';
import { Link } from 'react-router-dom';

// Définition des thèmes et sous-thèmes
const themesData = {
  Divers: { data: Object.values(unifiedData), subThemes: null },
  Géographie: {
    data: [
      ...Object.values(unifiedData).filter((q) => q.themes.includes('Géographie')),
      ...Object.values(unifiedData).filter((q) => q.themes.includes('Geographie')),
      ...Object.values(dataGeography).filter((q) => q.themes.includes('Géographie')),
    ],
    subThemes: ['Capitale', 'Drapeau', 'Monnaies'],
  },
  Histoire: {
    data: [
      ...Object.values(unifiedData).filter((q) => q.themes.includes('Histoire')),
      ...Object.values(NobelPrize).filter((q) => q.themes.includes('Histoire')),
    ],
    subThemes: null,
  },
  Art: {
    data: [
      ...Object.values(dataArt),
      ...Object.values(unifiedData).filter((q) => q.themes.includes('Art')),
    ],
    subThemes: ['Architecture', 'Sculpture', 'Peinture'],
  },
  'Littérature et Langues': {
    data: [
      ...Object.values(dataLiterature),
      ...Object.values(NobelPrize).filter((q) => q.themes.includes('Littérature')),
    ],
    subThemes: ['Littérature', 'Prix Goncourt'],
  },
  'Culture Pop': {
    data: Object.values(dataDivertissement),
    subThemes: ['Cinéma', 'Musique', 'Fort Boyard'],
  },
  'Science et Nature': {
    data: [
      ...Object.values(dataScience),
      ...Object.values(unifiedData).filter((q) => q.themes.includes('Nature')),
      ...Object.values(NobelPrize).filter((q) => q.themes.includes('Science')),
    ],
    subThemes: ['Nature', 'Astronomie', 'Prix Nobel', 'Médaille Fields', 'Prix Turing', 'Animaux'],
  },
  Sport: {
    data: Object.values(dataSport),
    subThemes: ['Football', 'Formule 1'],
  },
};

const themes = Object.keys(themesData);

const useUserStatistics = (userId) => {
  const [statistics, setStatistics] = useState({
    stats: { totalQuizzes: 0, badges: [], lastStudyDate: null },
    activity: { daily: {}, monthly: {} },
    performance: { byTheme: {} },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshStatistics = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const userDocRef = doc(db, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        const updatedStats = {
          stats: {
            totalQuizzes: data.stats?.totalQuizzes || 0,
            badges: data.stats?.badges || [],
            lastStudyDate: data.stats?.lastStudyDate || null,
          },
          activity: {
            daily: data.activity?.daily || {},
            monthly: data.activity?.monthly || {},
          },
          performance: {
            byTheme: data.performance?.byTheme || {},
          },
        };
        setStatistics(updatedStats);
        localStorage.setItem(`user_stats_${userId}`, JSON.stringify(updatedStats));
      } else {
        const initialData = {
          stats: { totalQuizzes: 0, badges: [], lastStudyDate: null },
          activity: { daily: {}, monthly: {} },
          performance: { byTheme: {} },
          profile: {
            uid: userId,
            email: auth.currentUser?.email || '',
            pseudo: auth.currentUser?.displayName || 'Utilisateur',
            createdAt: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
          },
        };
        await setDoc(userDocRef, initialData, { merge: true });
        setStatistics(initialData);
        localStorage.setItem(`user_stats_${userId}`, JSON.stringify(initialData));
      }
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      setError('Erreur lors du chargement des statistiques');
      setLoading(false);
    }
  }, [userId]);

  return { statistics, loading, error, refreshStatistics };
};

const useSubscriptionStatus = (userId) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState('loading');
  const [willEndAt, setWillEndAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!userId) {
        setSubscriptionStatus('not_subscribed');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();

        let status = userData?.subscription?.status || 'not_subscribed';
        let willEndAtDate = null;

        if (userData?.subscription?.willEndAt instanceof Timestamp) {
          willEndAtDate = userData.subscription.willEndAt.toDate();
        } else if (typeof userData?.subscription?.willEndAt === 'string') {
          willEndAtDate = new Date(userData.subscription.willEndAt);
        }

        if (status === 'cancelling' && willEndAtDate && new Date() > willEndAtDate) {
          status = 'not_subscribed';
          await setDoc(
            userRef,
            {
              subscription: {
                status: 'not_subscribed',
                updatedAt: new Date(),
                willEndAt: null,
              },
            },
            { merge: true }
          );
        }

        setSubscriptionStatus(status);
        setWillEndAt(willEndAtDate);
        setLoading(false);
      } catch (error) {
        console.error("Erreur de vérification d'abonnement:", error);
        setError(error.message);
        setSubscriptionStatus('error');
        setLoading(false);
      }
    };

    checkSubscription();
  }, [userId]);

  return { subscriptionStatus, willEndAt, loading, error };
};

const useLeaderboard = () => {
  const [leaderboards, setLeaderboards] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLeaderboards = useCallback(async () => {
    setLoading(true);
    console.log('[Leaderboard] Début de la récupération des leaderboards');
    try {
      const cachedLeaderboards = localStorage.getItem('leaderboards');
      if (cachedLeaderboards) {
        console.log('[Leaderboard] Données trouvées dans le cache local:', JSON.parse(cachedLeaderboards));
        setLeaderboards(JSON.parse(cachedLeaderboards));
      }

      const leaderboardCollection = collection(db, 'leaderboard');
      const leaderboardDocs = await getDocs(leaderboardCollection);

      const newLeaderboards = {};
      leaderboardDocs.forEach((doc) => {
        const theme = doc.id;
        const leaderboardData = doc.data().scores || [];
        newLeaderboards[theme] = leaderboardData;
      });
      console.log('[Leaderboard] Données récupérées depuis Firestore:', newLeaderboards);

      setLeaderboards(newLeaderboards);
      localStorage.setItem('leaderboards', JSON.stringify(newLeaderboards));
      setLoading(false);
    } catch (error) {
      console.error('[Leaderboard] Erreur lors de la récupération des leaderboards:', error);
      setError('Erreur lors de la récupération des leaderboards');
      setLoading(false);
    }
  }, []);

  return { leaderboards, loading, error, fetchLeaderboards, setLeaderboards };
};

const Quizz = () => {
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [selectedSubTheme, setSelectedSubTheme] = useState(null);
  const [randomQuestions, setRandomQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [questionStartTimes, setQuestionStartTimes] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [userId, setUserId] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [quizLimitReached, setQuizLimitReached] = useState(false);
  const [showLimitMessage, setShowLimitMessage] = useState(false);

  const { statistics, loading: statsLoading, error: statsError, refreshStatistics } = useUserStatistics(userId);
  const { subscriptionStatus, willEndAt, loading: subLoading, error: subError } = useSubscriptionStatus(userId);
  const { leaderboards, loading: leaderboardLoading, error: leaderboardError, fetchLeaderboards, setLeaderboards } = useLeaderboard();

  const NUM_QUESTIONS = 20;

  const getScore = useMemo(
    () => (answers) => {
      return answers.reduce((score, answer, index) => {
        if (answer === undefined || !randomQuestions[index]) return score;
        const isCorrect = answer.answerIndex === randomQuestions[index].correctAnswer;
        if (!isCorrect) return score;
        const timeTaken = answer.timeTaken / 1000; // Convert to seconds
        const maxTime = 60; // 60 seconds
        const maxScore = 10000;
        const minScore = 5000;
        const decayFactor = Math.pow(timeTaken / maxTime, 0.75); // More aggressive decay
        const questionScore = Math.round(minScore + (maxScore - minScore) * (1 - decayFactor));
        return score + questionScore;
      }, 0);
    },
    [randomQuestions]
  );

  const updateLeaderboard = useCallback(
    async (score, userId, leaderboardKey, currentLeaderboard, pseudo) => {
      console.log('[Leaderboard] Tentative de mise à jour du leaderboard', { score, userId, leaderboardKey, pseudo });
      try {
        const batch = writeBatch(db);
        const leaderboardRef = doc(db, 'leaderboard', leaderboardKey);
        const leaderboardSnap = await getDoc(leaderboardRef);

        let leaderboardData = leaderboardSnap.exists() ? leaderboardSnap.data().scores || [] : [];
        console.log('[Leaderboard] Données actuelles du leaderboard pour', leaderboardKey, ':', leaderboardData);

        const userIndex = leaderboardData.findIndex((entry) => entry.userId === userId);
        const lastScoreInLeaderboard = leaderboardData.length === 10 ? leaderboardData[9].score : -1;
        const userBestScore = userIndex !== -1 ? leaderboardData[userIndex].score : -1;

        console.log('[Leaderboard] Vérification:', { score, userBestScore, lastScoreInLeaderboard });
        if (score <= userBestScore && score <= lastScoreInLeaderboard) {
          console.log('[Leaderboard] Score insuffisant pour mise à jour, aucune action effectuée');
          return false;
        }

        const now = new Date().toISOString();
        if (userIndex !== -1) {
          if (score > userBestScore) {
            leaderboardData[userIndex] = { score, userId, pseudo, lastUpdated: now };
            console.log('[Leaderboard] Mise à jour du score existant pour l’utilisateur:', leaderboardData[userIndex]);
          }
        } else {
          leaderboardData.push({ score, userId, pseudo, lastUpdated: now });
          console.log('[Leaderboard] Nouvel utilisateur ajouté:', { score, userId, pseudo, lastUpdated: now });
        }

        leaderboardData.sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score;
          }
          return new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime();
        });
        leaderboardData = leaderboardData.slice(0, 10);
        console.log('[Leaderboard] Leaderboard trié et limité à 10:', leaderboardData);

        batch.set(leaderboardRef, { scores: leaderboardData });
        await batch.commit();
        console.log('[Leaderboard] Mise à jour envoyée à Firestore avec succès');

        setLeaderboards((prevLeaderboards) => {
          const updatedLeaderboards = { ...prevLeaderboards, [leaderboardKey]: leaderboardData };
          localStorage.setItem('leaderboards', JSON.stringify(updatedLeaderboards));
          console.log('[Leaderboard] État local et cache mis à jour:', updatedLeaderboards);
          return updatedLeaderboards;
        });
        return true;
      } catch (error) {
        console.error('[Leaderboard] Erreur lors de la mise à jour du leaderboard:', error);
        return false;
      }
    },
    [setLeaderboards]
  );

  const updateUserStatistics = useCallback(
    async (finalAnswers) => {
      console.log('[updateUserStatistics] Début de la mise à jour des statistiques');
      if (!userId || !selectedTheme || !startTime) {
        console.log('[updateUserStatistics] Données manquantes:', { userId, selectedTheme, startTime });
        return;
      }
  
      try {
        const batch = writeBatch(db);
        const userDocRef = doc(db, 'users', userId);
        const userDocSnap = await getDoc(userDocRef);
  
        console.log('[updateUserStatistics] userDocSnap existe ?', userDocSnap.exists());
        const userData = userDocSnap.exists() ? userDocSnap.data() : {};
        console.log('[updateUserStatistics] Données brutes de Firestore:', userData);
  
        const score = getScore(finalAnswers);
        const totalQuestions = randomQuestions.length;
        const today = new Date().toISOString().split('T')[0];
        const monthKey = new Date().toISOString().slice(0, 7);
        const timeSpent = Math.round((Date.now() - startTime) / 60000);
  
        const stats = {
          totalQuizzes: (userData.stats?.totalQuizzes || 0) + 1,
          badges: userData.stats?.badges || [],
          lastStudyDate: today,
        };
        if (stats.totalQuizzes >= 100 && !stats.badges.includes('100_quizzes')) {
          stats.badges.push('100_quizzes');
        }
        if (score >= totalQuestions * 5000 && !stats.badges.includes('perfect_score')) {
          stats.badges.push('perfect_score');
        }
  
        const daily = userData.activity?.daily || {};
        daily[today] = daily[today] || { quizzes: 0, flashcards: 0, timeSpent: 0 };
        daily[today].quizzes = (daily[today].quizzes || 0) + 1;
        daily[today].timeSpent = (daily[today].timeSpent || 0) + timeSpent;
  
        const monthly = userData.activity?.monthly || {};
        monthly[monthKey] = monthly[monthKey] || { quizCount: 0, flashcards: 0, averageScore: 0 };
        const currentMonth = monthly[monthKey];
        currentMonth.quizCount = (currentMonth.quizCount || 0) + 1;
        currentMonth.averageScore =
          ((currentMonth.averageScore * (currentMonth.quizCount - 1) + score / (totalQuestions * 10000)) * 100) / currentMonth.quizCount;
  
        const byTheme = userData.performance?.byTheme || {};
        const performanceKey = selectedSubTheme || selectedTheme;
        byTheme[performanceKey] = byTheme[performanceKey] || { bestScore: 0, averageScore: 0, attempts: 0, lastPlayed: null };
        const themeStats = byTheme[performanceKey];
  
        const oldBestScore = themeStats.bestScore || 0;
  
        themeStats.attempts = (themeStats.attempts || 0) + 1;
        themeStats.bestScore = Math.max(oldBestScore, score);
        themeStats.averageScore = (themeStats.averageScore * (themeStats.attempts - 1) + score) / themeStats.attempts;
        themeStats.lastPlayed = today;
  
        const pseudo = userData.pseudo || userData.profile?.pseudo || 'Utilisateur';
        console.log('[updateUserStatistics] Pseudo utilisé:', pseudo);
  
        const currentLeaderboard = leaderboards[performanceKey] || [];
        console.log('[updateUserStatistics] Vérification leaderboard:', {
          score,
          oldBestScore,
          attempts: themeStats.attempts,
          performanceKey,
        });
  
        if (score > oldBestScore || themeStats.attempts === 1) {
          await updateLeaderboard(score, userId, performanceKey, currentLeaderboard, pseudo);
        }
  
        batch.set(
          userDocRef,
          {
            stats,
            activity: { daily, monthly },
            performance: { byTheme },
          },
          { merge: true }
        );
        await batch.commit();
        console.log('[updateUserStatistics] Statistiques mises à jour dans Firestore');
  
        const updatedUserData = {
          ...userData,
          stats,
          activity: { daily, monthly },
          performance: { byTheme },
        };
        localStorage.setItem(`learnData_${userId}`, JSON.stringify({
          userData: updatedUserData,
          learnData: userData.learnData || {},
        }));
        console.log('[updateUserStatistics] localStorage mis à jour avec quizzes:', updatedUserData.activity.daily[today]);
  
        await refreshStatistics();
      } catch (error) {
        console.error('Erreur lors de la mise à jour des statistiques:', error);
      }
    },
    [userId, selectedTheme, selectedSubTheme, randomQuestions, startTime, getScore, refreshStatistics, updateLeaderboard, leaderboards]
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
        refreshStatistics();
      } else {
        setUserId(null);
      }
    });
    return () => unsubscribe();
  }, [refreshStatistics]);

  useEffect(() => {
    fetchLeaderboards();
  }, [fetchLeaderboards]);

  useEffect(() => {
    if (statsLoading || subLoading || !statistics.activity.daily) return;
  
    const today = new Date().toISOString().split('T')[0];
    const dailyQuizzes = statistics.activity.daily[today]?.quizzes || 0;
    const isSubscribed =
      subscriptionStatus === 'active' || 
      (subscriptionStatus === 'cancelling' && willEndAt && new Date() <= willEndAt);
  
    console.log('[QuizLimit] Vérification:', { dailyQuizzes, isSubscribed, subscriptionStatus, willEndAt });
    if (!isSubscribed && dailyQuizzes >= 5) {
      setQuizLimitReached(true);
    } else {
      setQuizLimitReached(false);
      setShowLimitMessage(false);
    }
  }, [statsLoading, subLoading, statistics.activity.daily, subscriptionStatus, willEndAt]);

  useEffect(() => {
    if (quizLimitReached || !selectedTheme || (themesData[selectedTheme].subThemes && !selectedSubTheme)) {
      console.log('[useEffect Questions] Bloqué:', { quizLimitReached, selectedTheme, selectedSubTheme });
      return;
    }

    console.log('[useEffect Questions] Génération des questions pour:', { selectedTheme, selectedSubTheme });
    const data = themesData[selectedTheme].data;
    const quizQuestions = selectedSubTheme ? data.filter((q) => q.themes.includes(selectedSubTheme)) : data;
    const shuffledQuestions = quizQuestions.sort(() => Math.random() - 0.5).slice(0, Math.min(NUM_QUESTIONS, quizQuestions.length));
    setRandomQuestions(shuffledQuestions);
    setCurrentQuestionIndex(0);
    setSelectedAnswers(new Array(shuffledQuestions.length).fill(undefined));
    setQuestionStartTimes(new Array(shuffledQuestions.length).fill(null));
    setShowResults(false);
    setStartTime(Date.now());
    setQuestionStartTimes((prev) => {
      const newTimes = [...prev];
      newTimes[0] = Date.now();
      return newTimes;
    });
  }, [selectedTheme, selectedSubTheme, quizLimitReached]);

  const handleThemeChange = useCallback(
    (theme) => {
      if (quizLimitReached) {
        console.log('[handleThemeChange] Limite atteinte, affichage du message');
        setShowLimitMessage(true);
        return;
      }
      console.log('[handleThemeChange] Changement de thème:', theme);
      setSelectedTheme(theme);
      setShowLimitMessage(false);
      const subThemes = themesData[theme].subThemes;
      if (subThemes && subThemes.length === 1) {
        setSelectedSubTheme(subThemes[0]);
      } else {
        setSelectedSubTheme(null);
      }
      setRandomQuestions([]);
      setCurrentQuestionIndex(0);
      setSelectedAnswers([]);
      setQuestionStartTimes([]);
      setShowResults(false);
    },
    [quizLimitReached]
  );

  const handleSubThemeChange = useCallback(
    (subTheme) => {
      if (quizLimitReached) {
        console.log('[handleSubThemeChange] Limite atteinte, affichage du message');
        setShowLimitMessage(true);
        return;
      }
      console.log('[handleSubThemeChange] Changement de sous-thème:', subTheme);
      setSelectedSubTheme(subTheme);
      setShowLimitMessage(false);
    },
    [quizLimitReached]
  );

  const handleBackToThemes = useCallback(() => {
    console.log('[handleBackToThemes] Retour aux thèmes');
    setSelectedTheme(null);
    setSelectedSubTheme(null);
    setRandomQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setQuestionStartTimes([]);
    setShowResults(false);
    setStartTime(null);
    setShowLimitMessage(false);
  }, []);

  const handleAnswerClick = useCallback(
    (answerIndex) => {
      console.log('[handleAnswerClick] Réponse sélectionnée:', answerIndex, 'Question:', currentQuestionIndex + 1);
      const timeTaken = Date.now() - questionStartTimes[currentQuestionIndex];
      const updatedAnswers = [...selectedAnswers];
      updatedAnswers[currentQuestionIndex] = { answerIndex, timeTaken };
      setSelectedAnswers(updatedAnswers);

      if (currentQuestionIndex + 1 < randomQuestions.length) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setQuestionStartTimes((prev) => {
          const newTimes = [...prev];
          newTimes[currentQuestionIndex + 1] = Date.now();
          return newTimes;
        });
      } else {
        console.log('[handleAnswerClick] Fin du quiz, affichage des résultats');
        setShowResults(true);
        updateUserStatistics(updatedAnswers);
      }
    },
    [currentQuestionIndex, selectedAnswers, randomQuestions, questionStartTimes, updateUserStatistics]
  );

  const handleRetakeQuiz = useCallback(() => {
    console.log('[handleRetakeQuiz] Reprise du quiz');
    if (quizLimitReached) {
      console.log('[handleRetakeQuiz] Limite atteinte, affichage du message');
      setShowLimitMessage(true);
      return;
    }
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setQuestionStartTimes([]);
    setShowResults(false);
    setShowLimitMessage(false);
    const data = themesData[selectedTheme].data;
    const quizQuestions = selectedSubTheme ? data.filter((q) => q.themes.includes(selectedSubTheme)) : data;
    const shuffledQuestions = quizQuestions.sort(() => Math.random() - 0.5).slice(0, Math.min(NUM_QUESTIONS, quizQuestions.length));
    setRandomQuestions(shuffledQuestions);
    setStartTime(Date.now());
    setQuestionStartTimes(new Array(shuffledQuestions.length).fill(null).map((_, index) => index === 0 ? Date.now() : null));
  }, [selectedTheme, selectedSubTheme, quizLimitReached]);

  const loadingMessages = ['Chargement en cours...', 'Les questions arrivent...', 'Patientez un instant...'];
  const randomLoadingMessage = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];

  const progressPercentage = randomQuestions.length > 0 ? ((currentQuestionIndex + 1) / randomQuestions.length) * 100 : 0;

  const otherThemes = themes.filter((theme) => theme !== 'Divers');
  const chunkThemes = (array, size) => {
    const chunked = [];
    for (let i = 0; i < array.length; i += size) {
      chunked.push(array.slice(i, i + size));
    }
    return chunked;
  };

  if (subError || statsError || leaderboardError) {
    return (
      <div className="quizz-container">
        <div className="error-message">
          <p>Une erreur est survenue. Veuillez réessayer plus tard.</p>
        </div>
      </div>
    );
  }

  if (subLoading || statsLoading || leaderboardLoading) {
    return (
      <div className="quizz-container">
        <div className="loading-questions">{randomLoadingMessage}</div>
      </div>
    );
  }

  return (
    <div className="quizz-container">
      {showLimitMessage && (
        <div className="limit-reached">
          <h2>Limite atteinte</h2>
          <p>Vous avez atteint la limite de 5 quiz par jour pour les utilisateurs non abonnés.</p>
          <p>Passez à un abonnement Premium pour des quiz illimités !</p>
          <Link to="/account" className="subscribe-button">
            S'abonner
          </Link>
        </div>
      )}
      {!selectedTheme ? (
        <div className="theme-selection">
          <div className="general-theme">
            <div
              onClick={() => handleThemeChange('Divers')}
              className="theme-card theme-general"
            >
              <h2>Divers</h2>
            </div>
          </div>
          <div className="other-themes">
            {chunkThemes(otherThemes, 3).map((row, rowIdx) => (
              <div key={rowIdx} className={`theme-row ${row.length < 3 ? 'center-last' : ''}`}>
                {row.map((theme, idx) => {
                  const themeIndex = rowIdx * 3 + idx;
                  return (
                    <div
                      key={theme}
                      onClick={() => handleThemeChange(theme)}
                      className={`theme-card theme-${themeIndex}`}
                    >
                      <h2>{theme}</h2>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : !selectedSubTheme && themesData[selectedTheme].subThemes && themesData[selectedTheme].subThemes.length > 1 ? (
        <div className="subtheme-selection">
          <div className="theme-header">
            <button onClick={handleBackToThemes} className="back-button">
              ← Retour aux thèmes
            </button>
            <h1>{selectedTheme}</h1>
          </div>
          <div className="subthemes-list">
            {themesData[selectedTheme].subThemes.map((subTheme, index) => (
              <div
                key={subTheme}
                onClick={() => handleSubThemeChange(subTheme)}
                className={`subtheme-card theme-${index % 10}`}
              >
                <h3>{subTheme}</h3>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={`theme-content theme-${themes.indexOf(selectedTheme) % 10}`}>
          <div className="theme-header">
            <button onClick={handleBackToThemes} className="back-button">
              ← Retour aux thèmes
            </button>
            <h1>{selectedSubTheme || selectedTheme}</h1>
          </div>
          <div className="content">
            {showResults ? (
              <>
                <QuizResults
                  score={getScore(selectedAnswers)}
                  totalQuestions={randomQuestions.length}
                  randomQuestions={randomQuestions}
                  selectedAnswers={selectedAnswers}
                  onRetakeQuiz={handleRetakeQuiz}
                  leaderboard={leaderboards[selectedSubTheme || selectedTheme] || []}
                  totalQuizzes={statistics.stats.totalQuizzes}
                />
                {quizLimitReached && (
                  <div className="limit-reached">
                    <p>Vous avez atteint la limite de 5 quiz par jour pour les utilisateurs non abonnés.</p>
                    <p>Passez à un abonnement Premium pour des quiz illimités !</p>
                    <Link to="/account" className="subscribe-button">
                      S'abonner
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="progress-info">
                  <p className="question-counter">{`Question ${currentQuestionIndex + 1}/${randomQuestions.length}`}</p>
                  <div className="progress-bar-container">
                    <div className="progress-bar" style={{ width: `${progressPercentage}%` }} data-progress={progressPercentage}></div>
                  </div>
                </div>
                {randomQuestions.length > 0 ? (
                  <QuizQuestion
                    key={`${currentQuestionIndex}-${randomQuestions[currentQuestionIndex].question}`}
                    question={randomQuestions[currentQuestionIndex].question}
                    answers={randomQuestions[currentQuestionIndex].answers}
                    correctAnswer={randomQuestions[currentQuestionIndex].correctAnswer}
                    onAnswerClick={handleAnswerClick}
                    isLocked={selectedAnswers[currentQuestionIndex] !== undefined}
                    feedback={null}
                    image={randomQuestions[currentQuestionIndex].image || null}
                    sound={randomQuestions[currentQuestionIndex].sound || null}
                  />
                ) : (
                  <div className="loading-questions">Chargement des questions...</div>
                )}
              </>
            )}
          </div>
          <div className="leaderboard">
            {!leaderboardLoading && (selectedSubTheme || selectedTheme) ? (
              <Leaderboard leaderboard={leaderboards[selectedSubTheme || selectedTheme] || []} userId={userId} />
            ) : (
              <div style={{ height: '100%' }}></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(Quizz);